import React, { useState, useEffect } from "react";
import Moment from "react-moment";
import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Divider,
  Stack,
} from "@mui/material";
import CardioRIcon from "./CardioRIcon";
import Pagination from "./Pagination";

function Table({
  measurment,
  currentPage,
  pages,
  updatePageNumber,
  kind,
  selectRow,
  updateSelectRow,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [rowSelected, setRowSelected] = useState(measurment && measurment[0]);

  // פונקציית עזר לצבע טקסט דינמי שדורס CSS ישן
  const textColor = theme.palette.text.primary;
  const secondaryColor = theme.palette.text.secondary;

  const selectRowClick = (selected) => {
    setRowSelected(selected);
    if (["ecgs", "invoices", "hafnayot"].includes(kind)) {
      selectRow(selected);
    }
  };

  useEffect(() => {
    if (updateSelectRow !== rowSelected) {
      setRowSelected(updateSelectRow);
    }
  }, [updateSelectRow]);

  const dateFormat =
    kind !== "invoices" && kind !== "hafnayot"
      ? "DD/MM/YYYY HH:mm"
      : "DD/MM/YYYY";

  const isSelected = (m) => {
    if (!rowSelected) return false;
    if (kind === "ecgs") return rowSelected.devdatetime === m.devdatetime;
    if (kind === "hafnayot") return rowSelected.docName === m.docName;
    if (kind === "invoices") return rowSelected.docNum === m.docNum;
    return false;
  };

  // רכיב קטן לשורת מידע במובייל (Key-Value באותה שורה)
  const MobileRow = ({ label, value, isBold = false }) => (
    <Stack
      direction="row"
      justifyContent="flex-start"
      spacing={2}
      sx={{ width: "100%", mb: 0.5 }}
    >
      <Typography
        variant="caption"
        sx={{ color: secondaryColor, whiteSpace: "nowrap" }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: textColor,
          fontWeight: isBold ? 700 : 400,
          textAlign: "right",
        }}
      >
        {value}
      </Typography>
    </Stack>
  );

  return (
    <Box sx={{ width: "100%", mt: 2 }}>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          bgcolor: "background.paper",
          borderRadius: "16px",
          overflow: "hidden",
          border: `1px solid ${theme.palette.divider}`,
          backgroundImage: "none", // מבטל גרדיאנט של MUI ב-Dark Mode
        }}
      >
        <MuiTable>
          {!isMobile && (
            <TableHead sx={{ bgcolor: "primary.main" }}>
              <TableRow>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                  #
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                  תאריך
                </TableCell>
                {kind !== "invoices" && kind !== "hafnayot" && (
                  <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                    שם מלא
                  </TableCell>
                )}
                {kind === "invoices" && (
                  <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                    סוג
                  </TableCell>
                )}
                {kind === "bloodP" && (
                  <>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      Sys/Dia
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      דופק
                    </TableCell>
                  </>
                )}
                {kind === "weight" && (
                  <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                    משקל
                  </TableCell>
                )}
                {kind === "activities" && (
                  <>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      תיאור
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      תוצאה
                    </TableCell>
                  </>
                )}
                {kind === "cardioR" && (
                  <>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      סימפטומים
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      קצב
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      ממצאים
                    </TableCell>
                  </>
                )}
                {kind === "cardioR" && (
                  <>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      דוח ראשוני
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      דוח סופי
                    </TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
          )}

          <TableBody>
            {measurment?.map((m, i) => {
              const selected = isSelected(m);

              if (isMobile) {
                return (
                  <Box
                    key={i}
                    onClick={() => selectRowClick(m)}
                    sx={{
                      p: 2,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      bgcolor: selected
                        ? "action.selected"
                        : "background.paper",
                      cursor: "pointer",
                      transition: "0.2s",
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ mb: 1 }}
                    >
                      <Typography
                        variant="subtitle2"
                        color="primary"
                        sx={{ fontWeight: 800 }}
                      >
                        #{i + 1}
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, color: textColor }}
                      >
                        <Moment date={m.devdatetime} format={dateFormat} />
                      </Typography>
                    </Stack>

                    <Stack spacing={0.5}>
                      {kind !== "invoices" && kind !== "hafnayot" && m.name && (
                        <MobileRow label="שם מלא:" value={m.name} />
                      )}
                      {kind === "invoices" && (
                        <MobileRow label="סוג חשבונית:" value={m.title} />
                      )}
                      {kind === "bloodP" && (
                        <>
                          <MobileRow
                            label="לחץ דם (Sys/Dia):"
                            value={`${m.sys}/${m.dia}`}
                            isBold
                          />
                          <MobileRow label="דופק:" value={m.pulse} />
                        </>
                      )}
                      {kind === "weight" && (
                        <MobileRow
                          label="משקל:"
                          value={`${m.weightdata} ק"ג`}
                          isBold
                        />
                      )}
                      {kind === "activities" && (
                        <>
                          <MobileRow label="תיאור:" value={m.complaindesc} />
                          <MobileRow label="תוצאה:" value={m.ctrlaction} />
                        </>
                      )}
                      {kind === "cardioR" && (
                        <>
                          <MobileRow label="קצב:" value={m.rate} />
                          <MobileRow
                            label="ממצאים:"
                            value={m.findingscodes?.slice(0, -1)}
                          />
                          <Stack
                            direction="row"
                            spacing={2}
                            justifyContent="flex-end"
                            sx={{ mt: 1 }}
                          >
                            {m.reportpath && (
                              <CardioRIcon
                                ifFinal={false}
                                reportId={m.reportId}
                              />
                            )}
                            {m.initReportPath && (
                              <CardioRIcon
                                ifFinal={true}
                                reportId={m.reportId}
                              />
                            )}
                          </Stack>
                        </>
                      )}
                      {m.ifAlert && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "error.main",
                            fontWeight: "bold",
                            mt: 1,
                          }}
                        >
                          ⚠️ חריגה במדד
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                );
              }

              return (
                <TableRow
                  key={i}
                  hover
                  onClick={() => selectRowClick(m)}
                  sx={{
                    cursor: "pointer",
                    bgcolor: selected ? "action.selected" : "inherit",
                    borderRight: m.ifAlert
                      ? `5px solid ${theme.palette.error.main}`
                      : "none",
                    "&:nth-of-type(odd)": {
                      bgcolor: selected ? "action.selected" : "action.hover",
                    },
                  }}
                >
                  <TableCell sx={{ color: textColor }}>{i + 1}</TableCell>
                  <TableCell sx={{ color: textColor }}>
                    <Moment date={m.devdatetime} format={dateFormat} />
                  </TableCell>
                  {kind !== "invoices" && kind !== "hafnayot" && (
                    <TableCell sx={{ color: textColor }}>{m.name}</TableCell>
                  )}
                  {kind === "invoices" && (
                    <TableCell sx={{ color: textColor }}>{m.title}</TableCell>
                  )}
                  {kind === "bloodP" && (
                    <>
                      <TableCell sx={{ color: textColor }}>
                        {m.sys}/{m.dia}
                      </TableCell>
                      <TableCell sx={{ color: textColor }}>{m.pulse}</TableCell>
                    </>
                  )}
                  {kind === "weight" && (
                    <TableCell sx={{ color: textColor }}>
                      {m.weightdata} ק"ג
                    </TableCell>
                  )}
                  {kind === "activities" && (
                    <>
                      <TableCell sx={{ color: textColor }}>
                        {m.complaindesc}
                      </TableCell>
                      <TableCell sx={{ color: textColor }}>
                        {m.ctrlaction}
                      </TableCell>
                    </>
                  )}
                  {kind === "cardioR" && (
                    <>
                      <TableCell sx={{ color: textColor }}>
                        {m.symptoms?.slice(0, -1)}
                      </TableCell>
                      <TableCell sx={{ color: textColor }}>{m.rate}</TableCell>
                      <TableCell sx={{ color: textColor }}>
                        {m.findingscodes?.slice(0, -1)}
                      </TableCell>
                      <TableCell>
                        {m.reportpath && (
                          <CardioRIcon ifFinal={false} reportId={m.reportId} />
                        )}
                      </TableCell>
                      <TableCell>
                        {m.initReportPath && (
                          <CardioRIcon ifFinal={true} reportId={m.reportId} />
                        )}
                      </TableCell>
                    </>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </MuiTable>
      </TableContainer>

      <Box sx={{ display: "flex", justifyContent: "center", mt: 3, pb: 4 }}>
        <Pagination
          pages={pages}
          currentPage={currentPage}
          updatePageNumber={updatePageNumber}
        />
      </Box>
    </Box>
  );
}

export default Table;
